// 상품 목록 표의 계산 함수 (2026-10-02 KST 추가)
//
// 화면과 떼어 둔 순수 함수들이다. 재고 합계, 대표 가격·마진율, 배송 태그, 상품 URL,
// 판매상태 판정과 상태를 바꾸려면 어떤 기존 액션(listActions)을 실행할지, CSV 생성.
import { type ProductListQuery } from "@dashboard/graphql";
import { FEE_TYPE_OPTIONS } from "@dashboard/shippingSettings/components/ShippingPolicyForm/ShippingPolicyForm";
import { parseShippingPolicyForm } from "@dashboard/shippingSettings/types";
import { type RelayToFlat } from "@dashboard/types";

import { type ProductListAction } from "../../utils/listActions";

export type ListProduct = RelayToFlat<ProductListQuery["products"]>[number];
type Money = { amount: number; currency: string };

// 스토어프론트 주소. api.env 의 STOREFRONT_URL 을 ShopProvider 가 shop.storefrontUrl 로 불러와 넣어 두고,
// 불러오기 전에는 기본 주소를 쓴다. 상품 링크를 만드는 곳이 많아 인자 대신 여기서 읽는다 (2026-11-01 KST 수정)
export const DEFAULT_STOREFRONT_BASE_URL = "https://farmstore.ai.kr";

let storefrontBaseUrl = DEFAULT_STOREFRONT_BASE_URL;

export function setStorefrontBaseUrl(value: string | null | undefined): void {
  storefrontBaseUrl = value?.trim().replace(/\/+$/, "") || DEFAULT_STOREFRONT_BASE_URL;
}

export const getStorefrontBaseUrl = (): string => storefrontBaseUrl;

const STOREFRONT_LOCALE = "ko";

/** 채널을 고르지 않았으면 첫 리스팅으로 대신한다 — 채널이 하나인 상점이다 */
export function getListing(product: ListProduct, channelId?: string) {
  return (
    product.channelListings?.find(item => item.channel.id === channelId) ??
    product.channelListings?.[0]
  );
}

// ── 재고 ────────────────────────────────────────────────

export interface StockSummary {
  quantity: number;
  allocated: number;
  /** 재고를 세는 변형이 하나도 없으면 수량이 뜻이 없다 */
  tracked: boolean;
}

export function sumStock(product: ListProduct): StockSummary {
  const variants = product.variants ?? [];
  const stocks = variants.flatMap(variant => variant.stocks ?? []);

  return {
    quantity: stocks.reduce((total, stock) => total + stock.quantity, 0),
    allocated: stocks.reduce((total, stock) => total + stock.quantityAllocated, 0),
    tracked: variants.some(variant => variant.trackInventory),
  };
}

// ── 가격 ────────────────────────────────────────────────

export interface PriceSummary {
  price: Money;
  costPrice: Money | null;
  priorPrice: Money | null;
  /** 판매가 대비 (판매가 - 공급가) 비율(%). 공급가가 없으면 null */
  marginRate: number | null;
  /** 변형마다 판매가가 달라 대표 가격 하나로 다 보여 줄 수 없는 경우 */
  hasRange: boolean;
}

/** 해당 채널에서 가장 싼 변형을 대표로 삼는다 — 스토어프론트의 '~부터' 가격과 같다 */
export function getPriceSummary(product: ListProduct, channelId?: string): PriceSummary | null {
  const listingChannelId = getListing(product, channelId)?.channel.id;
  const prices = (product.variants ?? []).flatMap(variant => {
    const listing = variant.channelListings?.find(item => item.channel.id === listingChannelId);

    return listing?.price
      ? [
          {
            price: listing.price,
            costPrice: listing.costPrice ?? null,
            priorPrice: listing.priorPrice ?? null,
          },
        ]
      : [];
  });

  if (prices.length === 0) {
    return null;
  }

  const cheapest = prices.reduce((min, item) =>
    item.price.amount < min.price.amount ? item : min,
  );
  const { price, costPrice } = cheapest;

  return {
    ...cheapest,
    marginRate:
      costPrice && price.amount > 0
        ? Math.round(((price.amount - costPrice.amount) / price.amount) * 100)
        : null,
    hasRange: prices.some(item => item.price.amount !== price.amount),
  };
}

// ── 면세/과세 (2026-10-03 KST 추가) ─────────────────────

const TAX_COUNTRY = "KR";

/**
 * 상품 세금 분류(없으면 상품 유형 분류)의 한국 세율이 0 이면 면세다. 스토어프론트 토스 결제의
 * 면세 금액도 같은 분류로 Saleor 가 계산한 세액 0 을 보고 정한다. 분류가 없거나 한국 세율이
 * 없으면 기본 세율(부가세 10%)이 걸리므로 과세다.
 */
export function isTaxFree(product: ListProduct): boolean {
  const taxClass = product.taxClass ?? product.productType?.taxClass;
  const rate = taxClass?.countries.find(item => item.country.code === TAX_COUNTRY)?.rate;

  return rate === 0;
}

// ── 배송 태그 ───────────────────────────────────────────

const feeTypeLabel = (value: string | undefined) => {
  if (!value) {
    return "";
  }

  try {
    const feeType = parseShippingPolicyForm(JSON.parse(value)).feeType;

    return FEE_TYPE_OPTIONS.find(option => option.value === feeType)?.label ?? "";
  } catch {
    return "";
  }
};

/**
 * "[공통]무료배송" / "[개별]고정 배송비" 처럼 상품이 어느 배송비 정책을 따르는지 보여 준다.
 * 상품 metadata 가 없거나 공통(common)이면 쇼핑몰 기본 배송 설정의 유형을 쓴다.
 */
export function getShippingTag(
  productPolicy: string | null | undefined,
  commonSettings: string | undefined,
): string {
  let individual = false;

  try {
    individual = !!productPolicy && JSON.parse(productPolicy)?.mode === "individual";
  } catch {
    individual = false;
  }

  if (individual) {
    return `[개별]${feeTypeLabel(productPolicy ?? undefined) || "배송비"}`;
  }

  // 공통 정책을 따르는 상품은 '[기본]' 대신 '[공통]' (2026-10-16 KST 수정)
  return `[공통]${feeTypeLabel(commonSettings) || "배송비"}`;
}

// ── URL ─────────────────────────────────────────────────

/** 스토어프론트 상품 주소 — 주문 목록에서도 쓴다 (2026-10-05 KST 분리) */
export const buildStorefrontProductUrl = (channelSlug: string, productSlug: string): string =>
  `${getStorefrontBaseUrl()}/${STOREFRONT_LOCALE}/${channelSlug}/products/${encodeURIComponent(productSlug)}`;

export function buildProductUrl(product: ListProduct, channelId?: string): string | null {
  const channelSlug = getListing(product, channelId)?.channel.slug;

  if (!product.slug || !channelSlug) {
    return null;
  }

  return buildStorefrontProductUrl(channelSlug, product.slug);
}

// ── 판매상태 ────────────────────────────────────────────

export type SaleStatus = "ON_SALE" | "PREPARING" | "SOLD_OUT";

export const SALE_STATUS_LABELS: Record<SaleStatus, string> = {
  ON_SALE: "판매중",
  PREPARING: "준비중",
  SOLD_OUT: "품절",
};

export const SALE_STATUS_OPTIONS = (Object.keys(SALE_STATUS_LABELS) as SaleStatus[]).map(value => ({
  value,
  label: SALE_STATUS_LABELS[value],
}));

export interface RowState {
  saleStatus: SaleStatus;
  visible: boolean;
}

interface StatusInput {
  soldOut: boolean;
  purchasable: boolean;
  visible: boolean;
}

export function getStatusInput(product: ListProduct, channelId?: string): StatusInput {
  const listing = getListing(product, channelId);

  return {
    soldOut: !!product.soldOutFlag,
    purchasable: !!listing?.isAvailableForPurchase,
    visible: !!listing?.visibleInListings,
  };
}

/** 품절 표식이 구매 가능 여부보다 앞선다 — 품절 처리해도 구매 가능 설정은 그대로 남는다 */
export function getRowState({ soldOut, purchasable, visible }: StatusInput): RowState {
  return {
    saleStatus: soldOut ? "SOLD_OUT" : purchasable ? "ON_SALE" : "PREPARING",
    visible,
  };
}

/** 지금 상태에서 목표 상태로 가기 위해 차례로 실행할 기존 액션들 */
export function planRowChange(current: StatusInput, target: RowState): ProductListAction[] {
  const actions: ProductListAction[] = [];

  if (target.saleStatus === "SOLD_OUT") {
    if (!current.soldOut) {
      actions.push("soldOut");
    }
  } else {
    if (current.soldOut) {
      actions.push("restoreSoldOut");
    }

    const wantPurchasable = target.saleStatus === "ON_SALE";

    if (current.purchasable !== wantPurchasable) {
      actions.push(wantPurchasable ? "purchasable" : "notPurchasable");
    }
  }

  if (current.visible !== target.visible) {
    actions.push(target.visible ? "showInListings" : "hideFromListings");
  }

  return actions;
}

/** 품절 해제가 구매 가능 변경보다, 구매 가능 변경이 품절 처리보다 먼저 가야 뜻이 맞는다 */
const SALE_ACTION_ORDER: ProductListAction[] = [
  "restoreSoldOut",
  "purchasable",
  "notPurchasable",
  "soldOut",
];

/**
 * 선택 상품을 판매상태 하나로 맞출 때 실행할 액션별 상품 목록. 목록 노출은 그대로 둔다.
 * 기존 액션은 상품 여러 개를 한 번에 받으므로 액션 단위로 묶는다.
 */
export function planBulkSaleStatus(
  products: ListProduct[],
  target: SaleStatus,
  channelId?: string,
): Array<{ action: ProductListAction; productIds: string[] }> {
  const plans = products.map(product => {
    const input = getStatusInput(product, channelId);

    return {
      id: product.id,
      actions: planRowChange(input, { saleStatus: target, visible: input.visible }),
    };
  });

  return SALE_ACTION_ORDER.map(action => ({
    action,
    productIds: plans.filter(plan => plan.actions.includes(action)).map(plan => plan.id),
  })).filter(step => step.productIds.length > 0);
}

// ── CSV ─────────────────────────────────────────────────

const pad = (value: number) => String(value).padStart(2, "0");

/** 표의 등록일·수정일 — 2026-09-30 */
export function formatDate(value: string | null | undefined): string {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function escapeCell(value: string | number): string {
  const text = String(value);

  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** 엑셀이 숫자로 바꿔 버리는 코드값(앞자리 0, 긴 숫자)을 글자로 고정한다 */
const asText = (value: string) => (value ? `="${value}"` : "");

const CSV_HEADER = [
  "상품명",
  "상품코드",
  "공급사",
  "카테고리",
  "상품그룹",
  "재고",
  "할당",
  "공급가",
  "이전가",
  "판매가",
  "마진율(%)",
  // 화면 명칭과 통일 (2026-10-13 KST 수정)
  "판매 상태",
  "상품목록 노출",
  "쇼핑몰 게시",
  "등록일",
  "수정일",
  "상품URL",
];

export function buildProductsCsv(products: ListProduct[], channelId?: string): string {
  const rows = products.map(product => {
    const stock = sumStock(product);
    const price = getPriceSummary(product, channelId);
    const input = getStatusInput(product, channelId);
    const state = getRowState(input);

    return [
      product.name,
      // 상품코드는 slug(PN + 8자리) (2026-10-03 KST 수정)
      asText(product.slug),
      product.supplier?.name ?? "",
      product.category?.name ?? "",
      (product.collections ?? []).map(collection => collection.name).join(", "),
      stock.quantity,
      stock.allocated,
      price?.costPrice?.amount ?? "",
      price?.priorPrice?.amount ?? "",
      price?.price.amount ?? "",
      price?.marginRate ?? "",
      SALE_STATUS_LABELS[state.saleStatus],
      state.visible ? "노출" : "숨김",
      getListing(product, channelId)?.isPublished ? "게시" : "미게시",
      formatDateTime(product.created),
      formatDateTime(product.updatedAt),
      buildProductUrl(product, channelId) ?? "",
    ];
  });

  // 엑셀이 UTF-8 로 읽도록 BOM 을 붙인다. 없으면 한글이 깨진다
  return "\uFEFF" + [CSV_HEADER, ...rows].map(row => row.map(escapeCell).join(",")).join("\r\n");
}
